import { useMemo } from 'react';

import { orderBy } from 'lodash';

import { useThemeStatusStore } from 'store/themeStatus.store';

import { SummaryCard } from 'api/queries/getProjectStatusScoreCards.query';

import { WidgetCard } from 'components/common';
import classes from 'components/widgets/ProjectSummary/components/CardsSection/CardsSection.module.scss';
import { maxHeight } from 'components/widgets/ProjectSummary/components/CardsSection/CardsSection.utils';
import { CardGraph } from 'components/widgets/ProjectSummary/components/CardsSection/components/CardGraph/CardGraph';
import {
    getColorByStatus,
    serializeData,
    serializeOptions,
} from 'components/widgets/ProjectSummary/components/CardsSection/components/QualityCard/QualityCard.utils';
import { QualityCardMetrics } from 'components/widgets/ProjectSummary/components/CardsSection/components/QualityCard/QualityCardMetrics';
import { NoApplicableData } from 'components/widgets/ProjectSummary/components/NoApplicableData/NoApplicableData';
import { ToggleCollapse } from 'components/widgets/ProjectSummary/components/ToggleCollapse/ToggleCollapse';

interface Props extends SummaryCard {
    collapse: boolean;
    setCollapse: (value: boolean) => void;
    chartHeight?: number | string;
    canExpand: boolean;
}

export const QualityCard = ({
    cardProperties,
    cardTitle,
    cardTrends,
    collapse,
    setCollapse,
    overallScore,
    chartHeight,
    canExpand,
}: Props) => {
    const sortedMetricsList = useMemo(() => orderBy(cardProperties.metrics, ['order', 'desc']), [cardProperties]);
    const showGraphSection = cardTrends?.[0];
    const height = !collapse && showGraphSection ? maxHeight : '100%';
    const status = cardTitle.status;
    const tooltipContent =
        status === 'N/A' ? 'Not applicable with applied filter' : `Overall Score is ${overallScore.value}`;
    const id = `statusSummary_quality`;

    const { themeStatus } = useThemeStatusStore();

    return (
        <WidgetCard
            titleColor={getColorByStatus({ status, theme: themeStatus })}
            titleText={cardTitle.title}
            scoreValue={status}
            contentHeight={height}
            tooltipContent={tooltipContent}
            id={id}
        >
            {status === 'N/A' && <NoApplicableData id={id} />}
            {status !== 'N/A' && (
                <div className={classes.cardWrapper}>
                    {canExpand && (
                        <div className={classes.topSection}>
                            <div className={`${classes.metricsListContainer} ${collapse && classes.collapse}`}>
                                <QualityCardMetrics sortedMetricsList={sortedMetricsList} id={id} />
                            </div>
                            <ToggleCollapse setCollapse={setCollapse} collapse={collapse} id={id} />
                        </div>
                    )}
                    {showGraphSection && (
                        <CardGraph
                            data={cardTrends}
                            xaxisTitle={'Version'}
                            yaxisTitle={'Quality Score (%)'}
                            yAxisMaxValue={100}
                            height={chartHeight}
                            serializeOptions={serializeOptions}
                            serializeData={serializeData}
                        />
                    )}
                </div>
            )}
        </WidgetCard>
    );
};
